<template>
    <c-inspection-data
        :has-data    = "true"
        status       = "X"
        :show-header = "false"
        :height      = "height"
        :show-border = "true"
        :title       = "$t('disposition.images.menu')"
    >
        <template v-slot:body>
            <div class="w-100 h-100 images-scroll">
                <input
                    ref      = "fileInput"
                    type     = "file"
                    multiple
                    hidden
                    :accept  = "accept"
                    @change  = "onFiles"
                />

                <div v-for="section in sections" :key="section.type" class="images-section">
                    <div class="section-head">
                        <span class="section-title">{{ section.title }}</span>
                        <span class="section-count">{{ imagesOf(section.type).length }}</span>
                        <v-btn text small color="primary" :disabled="!inspection" @click="onUpload(section.type)">
                            <v-icon left small>mdi-upload</v-icon>
                            {{ section.upload }}
                        </v-btn>
                    </div>

                    <div v-if="imagesOf(section.type).length === 0" class="section-empty">
                        {{ $t('disposition.images.empty') }}
                    </div>

                    <div v-else class="strip">
                        <div
                            v-for = "(image, index) in imagesOf(section.type)"
                            :key  = "image.id"
                            class = "strip-item"
                            @click = "onSelect(section.type, index)"
                        >
                            <c-inspection-image
                                :inspection-id = "inspection.id"
                                :image-id      = "image.id"
                            />
                        </div>
                    </div>
                </div>

                <img-lightbox
                    v-if      = "lightbox"
                    :images   = "imagesOf(lightbox)"
                    :position.sync = "position"
                    fullscreen
                    @close    = "lightbox = null"
                />
            </div>
        </template>
    </c-inspection-data>
</template>

<script>

import PAreaMixin       from "@/modules/dis/components/inspection/common/mixins/PAreaMixin";
import CInspectionImage from "@/modules/dis/components/inspection/common/components/CInspectionImage.vue";
import ImgLightbox      from "@/modules/img/components/main/ImgLightbox.vue";
import CInspectionData  from "@/modules/dis/components/inspection/common/components/CInspectionData.vue";
import Img              from "@/modules/img/components/service/Img";
import { CAPTURED, sortImages } from "@/modules/img/components/service/ImageSort";
import Uploads          from "@/modules/img/components/service/Uploads";
import { ACCEPT }       from "@/modules/img/components/service/UploadQueue";

/**
 * The images of an inspection: Fotos (imageType IMAGE) and Prüfbuch (BOOK), one strip each. The view
 * scrolls vertically, a strip horizontally.
 */
export default {
    name: "CInspectionImages",

    components: {
        CInspectionData,
        CInspectionImage,
        ImgLightbox,
    },

    mixins: [
        PAreaMixin
    ],

    data: () => ({
        images: [],
        // imageType of the open lightbox, null if closed
        lightbox: null,
        position: 0,
        // imageType the picked files are for
        uploadType: null,
        accept: ACCEPT,
        stopFileDone: null,
        reloadTimers: []
    }),

    computed: {
        sections () {
            return [
                { type: "IMAGE", title: this.$t("disposition.images.title"), upload: this.$t("disposition.images.upload") },
                { type: "BOOK",  title: this.$t("disposition.book.title"),   upload: this.$t("disposition.book.upload") },
            ];
        }
    },

    mounted() {
        this.load();
        // the inspection's file list may lag a moment behind the finished upload
        this.stopFileDone = Uploads.onFileDone(({ inspectionId }) => {
            if (this.inspection && inspectionId === this.inspection.id) {
                this.load();
                this.reloadTimers.push(setTimeout(() => this.load(), 2000));
            }
        });
    },

    beforeDestroy () {
        this.reloadTimers.forEach(clearTimeout);
        this.stopFileDone && this.stopFileDone();
    },

    watch: {
        inspection () {
            this.load();
        }
    },

    methods: {
        load () {
            if (!this.inspection) {
                return;
            }

            Img.inspectionImages(this.inspection.id).then(images => {
                this.images = images || [];
            })
        },

        imagesOf (type) {
            // by capture time, no manual sorting here
            return sortImages(this.images.filter(image => image.imageType === type), CAPTURED);
        },

        onSelect (type, index) {
            this.position = index;
            this.lightbox = type;
        },

        onUpload (type) {
            this.uploadType = type;
            this.$refs.fileInput.click();
        },

        onFiles (event) {
            const files = Array.from(event.target.files);
            event.target.value = "";
            if (files.length === 0 || !this.inspection || !this.uploadType) {
                return;
            }

            // the global upload queue shows progress and results (ImgUploadPanel)
            Uploads.add({
                inspectionId: this.inspection.id,
                imageType   : this.uploadType,
                siteLabel   : String(this.inspection.elevatorNo || this.inspection.id),
                folderLabel : this.sections.find(section => section.type === this.uploadType).title,
            }, files);
        },
    }
}
</script>

<style scoped>

.images-scroll {
    overflow-y: auto;
    padding: 4px 8px 16px 8px;
}

.images-section {
    margin-bottom: 16px;
}

.section-head {
    display: flex;
    align-items: center;
    padding-bottom: 4px;
}

.section-title {
    font-size: 16px;
    font-weight: 500;
}

.section-count {
    margin: 0 12px 0 8px;
    font-size: 13px;
    color: gray;
}

.section-empty {
    padding: 8px 0;
    color: gray;
}

/* always one row; more images than fit scroll sideways */
.strip {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 6px;
}

.strip-item {
    flex: 0 0 160px;
    width: 160px;
    height: 160px;
    margin-right: 8px;
    cursor: pointer;
}

</style>
